/**
 * Fixture classification + position constants for measurements.
 *
 * Measurement IDENTITY no longer lives here: it is one ref-based key in
 * `@shared-lib/dimensioning` (see
 * claude-docs/2026-09-15-unified-dimensioning.md). The three key helpers this
 * file used to carry — generateMeasurementKey / parseMeasurementKey /
 * isOldFormatKey — had no callers anywhere in the repo and were removed with
 * that move.
 */

/**
 * Fixture classification for elevation measurements
 * Custom fixtures get edge-to-edge measurements (left/right)
 * Non-custom fixtures get centerline measurements only
 * Furnishings are excluded from measurements entirely
 */
export const CUSTOM_FIXTURE_TYPES = new Set([
  'cabinet',
  'lower-cabinet',
  'upper-cabinet',
  'countertop',
  'shower-niche',
  'shower',
  'door',
  'window',
  'mirror',
]);

export const NON_CUSTOM_FIXTURE_TYPES = new Set([
  'sink',
  'toilet',
  'bathtub',
  // Both old (stove / oven) and new (range / wall-oven) names — saved
  // structures from before the GLB-aligned type rename flow through this
  // filter too.
  'stove',
  'range',
  'cooktop',
  'oven',
  'wall-oven',
  'dishwasher',
  'wall-light',
  'shower-controls',
  'shower-stem',
]);

export const FURNISHING_TYPES = new Set([
  'chair',
  'table',
  'bed',
  'sofa',
  'desk',
  'shelf',
  'refrigerator',
  'washer',
  'dryer',
]);

// Fixture types that only get horizontal measurements (no vertical)
// These are typically mounted on other fixtures (e.g., faucets on sinks)
export const HORIZONTAL_ONLY_FIXTURE_TYPES = new Set([
  'faucet',
  'bathroom-sink-faucet',
]);

export const MEASUREMENT_ORIENTATION = {
  VERTICAL: 1, // For vertical lines (left/right sides)
  HORIZONTAL: 2, // For horizontal lines (top/bottom)
};

export const MEASUREMENT_POSITION = {
  START: 1, // Left for vertical, Top for horizontal
  CENTER: 2, // Center for both
  END: 3, // Right for vertical, Bottom for horizontal
};

/**
 * Get a human-readable description of a measurement position
 * @param {number} orientation - MEASUREMENT_ORIENTATION value
 * @param {number} position - MEASUREMENT_POSITION value
 * @returns {string} Description
 */
export function getPositionDescription(orientation, position) {
  if (orientation === MEASUREMENT_ORIENTATION.VERTICAL) {
    switch (position) {
      case MEASUREMENT_POSITION.START:
        return 'left';
      case MEASUREMENT_POSITION.CENTER:
        return 'center';
      case MEASUREMENT_POSITION.END:
        return 'right';
      default:
        return 'unknown';
    }
  } else if (orientation === MEASUREMENT_ORIENTATION.HORIZONTAL) {
    switch (position) {
      case MEASUREMENT_POSITION.START:
        return 'top';
      case MEASUREMENT_POSITION.CENTER:
        return 'middle';
      case MEASUREMENT_POSITION.END:
        return 'bottom';
      default:
        return 'unknown';
    }
  }
  return 'unknown';
}
